<template>
  <div class="document-card-wrapper">
    <div
      :class="`document-card
        ${highlight ? highlight + '-highlight' : ''}
        ${background ? background + '-background' : ''}
        ${disabled ? 'document-card-disabled' : ''}`"
    >
      <NrFormLabel :for="id">
        <span class="document-card-label-sr-only">Select document</span>
        <input
          :id="id"
          class="document-card-checkbox"
          type="checkbox"
          v-model="internalChecked"
          :value="id"
          :checked="documentChecked"
          :disabled="checkboxDisabled || disabled"
        />
        <div class="document-card-checkmark">
          <NrTooltip
            v-if="checkboxDisabled"
            :label="'Individual documents cannot be deselected'"
            :parentTarget="true"
          />
        </div>
      </NrFormLabel>

      <div class="document-card-content-wrapper">
        <div class="document-card-header">
          <div v-if="disabled" class="documents-card-header-processing">
            Document Processing
          </div>
          <div
            v-if="documentExpirationLabel"
            class="documents-card-header-date"
          >
            <div>
              <NrTooltip :label="date" :parentTarget="true" />
              {{ documentExpirationLabel }}
            </div>
          </div>
          <div v-else class="documents-card-header-date">
            {{ date }}
          </div>
          <div class="document-card-header-format">
            {{ format }}
            <span
              v-show="visibility && visibility !== ''"
              :class="`${visibility && visibility.toLowerCase()}-visibility`"
              >{{ visibility }}</span
            >
          </div>
        </div>

        <div class="slot-content">
          <div v-if="editing" class="slot-content-title-editing">
            <NrFormInput
              hideLabel
              label="Edit document title"
              :id="`edit-doc-title-${id}`"
              v-model="editingTitleLocal"
            />
            <NrIconButton
              size="small"
              variant="minimal-neutral"
              :icon="{ name: 'check-circle', type: 'regular' }"
              aria-label="Edit title"
              class="nr-button link"
              @click="$emit('submit-edition', editingTitleLocal)"
            />
            <NrIconButton
              size="small"
              variant="minimal-neutral"
              :icon="{ name: 'times-circle', type: 'regular' }"
              aria-label="Cancel editing"
              class="nr-button link"
              @click="$emit('cancel-edit', false)"
            />
          </div>
          <NrButton
            v-else
            variant="minimal-neutral"
            @click="$emit('handle-click', id)"
            class="slot-content-btn"
          >
            <p class="slot-content-title">
              {{ title }}
            </p>
            <p class="slot-content-folder-name">
              {{ folderName }}
            </p>
          </NrButton>
          <slot v-if="!editing && !disabled" name="actions"></slot>
        </div>
        <div v-if="tags.length || pills.length" class="document-card-footer">
          <div class="nr-tags">
            <NrTag
              v-for="(tag, index) in tags"
              :key="`tag-${tag.type}-${index}`"
              @on-click="$emit('handle-tag-click', tag)"
              icon-position="left"
              :clickable="tag.clickable"
              :tag-color="tag.type"
              :label="`${getTagText(tag)}`"
              :aria-label="tag.aria_label"
              size="small"
              :icon="tag.icon"
            />
            <NrPill
              v-for="(pill, index) in pills"
              :key="`pill-${index}`"
              :label="pill.label"
              :pillColor="pill.color"
              :size="pill.size"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NrTag from '@nextrequestco/components/src/components/NrTag'
import NrPill from '@nextrequestco/components/src/components/NrPill'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrButton from '@nextrequestco/components/src/components/NrButton'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'

export default {
  name: 'DocumentCard',
  components: {
    NrTag,
    NrPill,
    NrTooltip,
    NrButton,
    NrIconButton,
    NrFormLabel,
    NrFormInput
  },
  props: {
    highlight: {
      type: String,
      default: 'default'
    },
    background: {
      type: String,
      default: 'default'
    },
    title: {
      type: String
    },
    folderName: {
      type: String
    },
    date: {
      type: String
    },
    format: {
      type: String
    },
    visibility: {
      type: String
    },
    tags: {
      type: Array,
      default: () => []
    },
    pills: {
      type: Array,
      default: () => []
    },
    editing: {
      type: Boolean,
      default: false
    },
    id: {
      type: Number
    },
    modelValue: {
      type: Array
    },
    documentChecked: {
      type: Boolean
    },
    disabled: {
      type: Boolean,
      default: false
    },
    checkboxDisabled: {
      type: Boolean,
      default: false
    },
    documentExpirationLabel: {
      type: String
    }
  },
  data() {
    return {
      editingTitleLocal: ''
    }
  },
  created() {
    this.editingTitleLocal = this.title
  },
  methods: {
    getTagText(tag) {
      const qty = tag.qty ? tag.qty + ' ' : ''
      const plural = qty > 1 ? 's' : ''

      if (qty) {
        return qty + tag.text + plural
      } else {
        return tag.text
      }
    }
  },
  computed: {
    internalChecked: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/colors.scss';
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.document-card {
  background: $white;
  display: flex;
  align-items: center;
  padding: 8px;
  &-label-sr-only {
    position: fixed;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }
  &-content {
    flex-grow: 1;
    cursor: pointer;
    width: 80%;
    &-wrapper {
      width: calc(100% - 37px);
    }
  }
  &-header {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    color: $grey;
  }
  &-checked {
    background: $white-ter;
  }
  input.document-card-checkbox {
    position: fixed;
    cursor: pointer;
    opacity: 0;
    height: 0;
    width: 0;
    &:checked ~ .document-card-checkmark {
      border: none;
      background-color: $color-purple-100;
    }
    &:checked ~ .document-card-checkmark:hover {
      border: none;
      background-color: $color-purple-80;
    }
    &:disabled ~ .document-card-checkmark {
      border: none;
      opacity: 0.2;
      cursor: default;
    }
    &:focus-visible ~ .document-card-checkmark {
      outline: $border-width-medium dotted $color-purple-110;
      outline-offset: $spacing-4;
    }
    &:checked ~ .document-card-checkmark:after {
      display: block;
    }
  }

  &-checkmark {
    position: relative;
    top: 0;
    left: 0;
    height: 20px;
    width: 20px;
    background-color: $white;
    border: 2px solid $color-purple-100;
    margin-right: 12px;
    border-radius: 4px;
    &:after {
      content: '';
      position: absolute;
      display: none;
      left: 7px;
      top: 2px;
      width: 7px;
      height: 12px;
      border: solid $white;
      border-width: 0 2px 2px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
    &:hover {
      border-color: $color-purple-80;
      cursor: pointer;
    }
  }
  .slot-content {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    &-btn {
      background: transparent;
      border: none;
      text-align: left;
      text-decoration: none;
      padding: 0;
      cursor: pointer;
      width: 1px;
      height: auto;
      min-width: 0;
      align-items: center;
      justify-content: flex-start;
      flex-grow: 1;
      flex-shrink: 1;
      flex-basis: 0;

      &:focus {
        outline: none;
      }
      &:hover,
      &:focus {
        .slot-content-title {
          color: $blue;
        }
      }
    }
    span {
      display: flex;
      min-width: 0;
      flex-direction: column;
    }
    &-title {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 16px;
      color: $grey-darker;
      margin-bottom: 0;
    }
    &-folder-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 16px;
      color: $grey;
      margin-bottom: 0;
    }
    &-title-editing {
      width: 100%;
      display: flex;
      .nr-field {
        flex: auto;
      }
      input {
        width: 100%;
        height: 35px;
        margin: 0;
        border: 1px solid $grey-light;
        border-radius: 4px;
        padding-left: 8px;
      }
      .nr-button {
        width: auto;
        padding: 0;

        &:not(:hover),
        &:not(:focus) {
          border-width: 0;
        }
      }
      .nr-icon {
        font-size: 24px;
        color: $grey;
        display: inline-block;
        margin: 4px;
        &:hover,
        &:focus {
          color: black;
        }
      }
    }
  }
  &-footer {
    display: flex;
    justify-content: space-between;

    .nr-tag {
      margin: 0 0.125rem;

      &:first-child {
        margin-left: unset;
      }

      &:last-child {
        margin-right: unset;
      }
    }
  }
  .public-visibility {
    margin-left: 8px;
    color: $green;
    font-weight: bold;
  }
  .requester-visibility {
    color: $blue;
    font-weight: bold;
  }

  .document-card-actions-rs,
  .document-card-actions-rr {
    opacity: 0;
  }

  &:hover,
  &:focus,
  &:focus-within {
    box-shadow:
      0 10px 20px rgba(0, 0, 0, 0.19),
      0 6px 6px rgba(0, 0, 0, 0.23);
    position: relative;

    .document-card-actions-rs,
    .document-card-actions-rr {
      opacity: 1;
    }
  }
  &-disabled {
    opacity: 0.4;
    & > * {
      cursor: default;
    }
    &:hover,
    &:focus {
      box-shadow: none;
      position: relative;
      z-index: 1;
    }
    .checkbox.checkbox[disabled] {
      cursor: default;
    }
  }
}

.none-highlight {
  border: 1px solid $white;
}
.red-highlight {
  border: 1px solid $red;
  border-left: 4px solid $red;
}
.purple-highlight {
  border: 1px solid $purple;
  border-left: 4px solid $purple;
}
.orange-highlight {
  border: 1px solid $orange;
  border-left: 4px solid $orange;
}
.yellow-highlight {
  border: 1px solid $yellow;
  border-left: 4px solid $yellow;
}
.green-highlight {
  border: 1px solid $green;
  border-left: 4px solid $green;
}
.default-highlight {
  border: 1px solid $white-ter;
  border-left: 4px solid $white-ter;
}
.blue-highlight {
  border: 1px solid $blue;
  border-left: 4px solid $blue;
}
.blue-100-highlight {
  border: 1px solid $color-blue-100;
  border-left: 4px solid $color-blue-100;
}
.black-highlight {
  border: 1px solid $black;
  border-left: 4px solid $black;
}

.green-background {
  background-color: #e1f5ed;
}

.purple-background {
  background-color: $color-purple-10;
}

.documents-card-header-processing {
  color: $red;
}
</style>
